import type { MutableRefObject } from "react";
import api from "../../../api/api";
import { responseError } from "../../../api/apiError";
import { getColumnLetter, pascalToSentenceCase } from "../../utils";
import { fillDrilldownCall, getFieldValue, type StringState } from "../ReportServices";

type ReportColumnDefinition = {
  field: string;
  type: string;
  name: string;
  filter: string;
  call: string;
};

type RegisterReportDrilldownOptions = {
  enableDrilldownLinksRef: MutableRefObject<boolean>;
  drilldownCancelTokenRef: MutableRefObject<string>;
  selectedItem: StringState;
  authToken: string;
  identityProvider: string;
  pageSize: number;
};

const buildReportDataBody = (
  call: string,
  pageIndex: number,
  pageSize: number
): Record<string, unknown> => {
  const callParts = call.split("&");
  const callEndpoint = callParts[0];
  const body: Record<string, unknown> = {
    apiEndpoint: callEndpoint,
    pageIndex,
    pageSize,
  };

  for (const part of callParts.slice(1)) {
    const eqIndex = part.indexOf("=");
    if (eqIndex > 0) {
      const key = part.substring(0, eqIndex);
      const value = part.substring(eqIndex + 1);
      body[key] = value;
    } else {
      body[part] = true;
    }
  }

  return body;
};

const getDrilldownPayloadFromSelectedRange = async (ctx: Excel.RequestContext): Promise<{ formula: string; range: Excel.Range } | null> => {
  const range = ctx.workbook.getSelectedRange();
  range.load(["rowIndex", "columnIndex", "formulas", "values", "address"]);

  const spillParent = range.getSpillParentOrNullObject();
  spillParent?.load(["address", "formulas", "values", "rowIndex", "columnIndex"]);

  await ctx.sync();

  let formula = String(range.formulas?.[0]?.[0] ?? range.values?.[0]?.[0] ?? "");
  let payload = /^=".*"&T\(N\("#\/drilldown\?/.test(formula);
  if (payload) return { formula, range };

  formula = String(spillParent.formulas?.[0]?.[0] ?? "");
  const nameMatch = formula.match(/^=\s*([A-Za-z_][A-Za-z0-9_\.]*)\s*$/);
  let name = "";
  let tableRange: Excel.Range | null = null;

  if (nameMatch) {
    name = nameMatch[1];
  } else {
    const tables = range.getTables();
    tables?.load(["items"]);
    await ctx.sync();

    const table = tables.items?.[0];
    table?.load(["name"]);
    await ctx.sync();

    tableRange = table.getRange();
    tableRange.load(["rowIndex", "columnIndex"]);
    await ctx.sync();

    const tableName = table?.name;
    if (!tableName) return null;

    const queries = ctx.workbook.queries;
    queries.load("items/name,items/formula");
    await ctx.sync();

    for (const query of queries.items) {
      if (query.name && tableName.toLowerCase().includes(query.name.toLowerCase())) {
        name = query.name;
        break;
      }
    }
    if (!name) return null;
  }

  const named = ctx.workbook.names.getItemOrNullObject(name);
  const srcRange = named.getRangeOrNullObject();
  named?.load(["name"]);
  srcRange?.load(["rowIndex", "columnIndex", "rowCount", "columnCount"]);
  await ctx.sync();
  if (named.isNullObject || srcRange.isNullObject) return null;

  const parentRow = spillParent.rowIndex ?? tableRange!.rowIndex;
  const parentCol = spillParent.columnIndex ?? tableRange!.columnIndex;
  const rowOffset = range.rowIndex - parentRow;
  const colOffset = range.columnIndex - parentCol;

  if (rowOffset < 0 || colOffset < 0) return null;
  if (rowOffset >= srcRange.rowCount || colOffset >= srcRange.columnCount) return null;

  const srcCell = srcRange.getCell(rowOffset, colOffset);
  srcCell.load(["address", "formulas", "values"]);
  await ctx.sync();

  formula = String(srcCell.formulas?.[0]?.[0] ?? srcCell.values?.[0]?.[0] ?? "");
  payload = /^=".*"&T\(N\("#\/drilldown\?/.test(formula);
  return payload ? { formula, range } : null;
};

const parseDrilldownPayload = (formula: string): { field: string; call: string } | null => {
  const payloadStart = formula.indexOf('("#/drilldown?') + 2;
  const payloadEnd = formula.indexOf('")', payloadStart);
  const payloadEncoded = formula.substring(payloadStart, payloadEnd);
  const payloadDecoded = decodeURIComponent(payloadEncoded);
  const fieldIndex = payloadDecoded.indexOf("f=");
  const callIndex = payloadDecoded.indexOf("&c=");
  const field = fieldIndex >= 0 && callIndex > fieldIndex ? payloadDecoded.substring(fieldIndex + 2, callIndex) : null;
  const call = callIndex >= 0 ? payloadDecoded.substring(callIndex + 3) : null;

  if (!field || !call) return null;
  return { field, call };
};

const readDrilldownColumns = async (ctx: Excel.RequestContext, field: string): Promise<ReportColumnDefinition[]> => {
  const specificSheetName = "(columns-" + field + ")";
  const specificSheet = ctx.workbook.worksheets.getItemOrNullObject(specificSheetName);
  await ctx.sync();

  const sheet = !specificSheet.isNullObject ? specificSheet : ctx.workbook.worksheets.getItem("(columns)");
  const range = sheet.getUsedRange();
  await ctx.sync();
  range.load("values");
  await ctx.sync();

  const columns: ReportColumnDefinition[] = range.values
    .slice(1)
    .map((row: any) => ({ field: row[0], type: row[1], name: row[2], filter: row[3], call: row[4] }));
  if (columns.length === 0) {
    columns.push({ field: "id", type: "@", name: "ID", filter: "", call: "" });
  }

  return columns;
};

const writeDrilldownHeaders = (drilldownSheet: Excel.Worksheet, columns: ReportColumnDefinition[]) => {
  const headerValues = columns.map((column) => (column.type === "drilldown" ? "" : column.name) || pascalToSentenceCase(column.field));
  const range = drilldownSheet.getRange(`A3:${getColumnLetter(columns.length)}3`);
  range.numberFormat = Array(1).fill(columns.map(() => "@"));
  range.values = [headerValues];
};

const writeDrilldownRows = async (
  drilldownSheet: Excel.Worksheet,
  columns: ReportColumnDefinition[],
  rows: any[],
  rowIndex: number,
  selectedItem: StringState
): Promise<number> => {
  const rowValues = rows.map((row: any) => columns.map((column) =>
    column.type === "drilldown" ? `="`
      + (column.name.length > 0 ? column.name : "Drill down")
      + `"&T(N("#/drilldown?f=` + column.filter + `&c=` + fillDrilldownCall(column.call, row, selectedItem) + `"))`:
    getFieldValue(row, column.field) ?? ""));
  const endRowIndex = rowIndex + rows.length;

  if (rows.length > 0) {
    const range = drilldownSheet.getRange(`A${rowIndex + 1}:${getColumnLetter(columns.length)}${endRowIndex}`);
    range.numberFormat = Array(rowValues.length).fill(columns.map((column) => (
      column.type === "drilldown" ? "" :
      column.type?.length > 0 ? column.type : "@")));
    range.formulas = rowValues;
  }

  return endRowIndex;
};

export async function registerReportDrilldownSelectionHandler({
  enableDrilldownLinksRef,
  drilldownCancelTokenRef,
  selectedItem,
  authToken,
  identityProvider,
  pageSize,
}: RegisterReportDrilldownOptions) {
  let selectionHandler: OfficeExtension.EventHandlerResult<Excel.SelectionChangedEventArgs> | null = null;

  async function onSelectionChanged() {
    if (!enableDrilldownLinksRef.current) return;

    await Excel.run(async (ctx) => {
      const payloadResult = await getDrilldownPayloadFromSelectedRange(ctx);
      if (!payloadResult) return;

      const { formula, range } = payloadResult;
      range.getOffsetRange(0, 1).select();
      await ctx.sync();

      console.log("Formula with drilldown payload selected at " + range.address + ": " + formula);
      const payload = parseDrilldownPayload(formula);
      if (!payload) {
        console.log("Drilldown payload missing f or c parameters");
        return;
      }

      const { field, call } = payload;
      console.log("Drilldown payload: f=" + field + ", c=" + call);

      const currentCancelToken = Date.now().toString();
      drilldownCancelTokenRef.current = currentCancelToken;

      const drilldownSheetName = "Drilldown-" + field;
      let drilldownSheet = ctx.workbook.worksheets.getItemOrNullObject(drilldownSheetName);
      await ctx.sync();
      if (!drilldownSheet.isNullObject) {
        drilldownSheet.delete();
        await ctx.sync();
      }

      drilldownSheet = ctx.workbook.worksheets.add(drilldownSheetName);
      drilldownSheet.position = 0;
      await ctx.sync();

      const body = buildReportDataBody(call, 0, pageSize);
      const response = await api.post("report/data", body, authToken, identityProvider);
      if (!response.ok) throw await responseError(response);

      const data: any = await response.json();
      const rows = data.rows;
      const totalCount = data.totalCount ?? 0;

      drilldownSheet.getRangeByIndexes(0, 0, 1, 1).values = [[`Drilldown / ${field} / ${body["FilterValue"] ?? ""}`]];
      drilldownSheet.getRangeByIndexes(0, 0, 1, 1).format.font.bold = true;
      await ctx.sync();

      const columns = await readDrilldownColumns(ctx, field);
      writeDrilldownHeaders(drilldownSheet, columns);

      let rowIndex = await writeDrilldownRows(drilldownSheet, columns, rows, 3, selectedItem);
      console.log(rows.length + " drilldown rows loaded, total count: " + totalCount);
      await ctx.sync();

      const usedRange = drilldownSheet.getRange("A3").getSurroundingRegion();
      drilldownSheet.tables.add(usedRange, true);
      await ctx.sync();

      drilldownSheet.activate();
      await ctx.sync();

      if (rows.length >= totalCount) return;

      console.log("Drilldown data loaded into sheet " + drilldownSheetName);
      let pageIndex = 1;
      while (rowIndex - 3 < totalCount) {
        if (drilldownCancelTokenRef.current !== currentCancelToken) {
          console.log("Drilldown cancelled due to new drilldown operation");
          break;
        }

        body.pageIndex = pageIndex;
        const pageResponse = await api.post("report/data", body, authToken, identityProvider);
        if (!pageResponse.ok) throw await responseError(pageResponse);

        const pageData: any = await pageResponse.json();
        rowIndex = await writeDrilldownRows(drilldownSheet, columns, pageData.rows, rowIndex, selectedItem);
        console.log(pageData.rows.length + " drilldown rows loaded, total count: " + totalCount);
        await ctx.sync();
        pageIndex++;
      }
    });
  }

  await Excel.run(async (ctx) => {
    const workbook = ctx.workbook;
    selectionHandler?.remove();
    selectionHandler = workbook.onSelectionChanged.add(onSelectionChanged);
    await ctx.sync();
  });
}
